import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Card, Box, Typography } from '@material-ui/core';
import {
  Psychology as PsychologyIcon,
  Calculate as CalculateIcon,
  RotateRight as RotateRightIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { RootState } from 'redux/store';
import { fetchMindgageMetrics } from 'redux/slices/assessment';
import useLocales from 'hooks/useLocales';
import CultureProfileCharts from './CultureProfileCharts';
import { Title1, Description2 } from './text/title';

interface CognitiveAbilitySectionProps {
  candidate: any;
  profile?: any;
  jobItem?: any; // Job post item that contains mindgage_metrics
}

export const CognitiveAbilitySection = ({
  candidate,
  profile,
  jobItem
}: CognitiveAbilitySectionProps): JSX.Element | null => {
  const { t, currentLang } = useLocales();
  const dispatch = useDispatch();
  const { mindgageMetrics, loading: loadingMetrics } = useSelector(
    (state: RootState) => state.assessment
  );

  const mindgage = candidate?.mainInfo?.mindgage || candidate?.mindgage;
  const hasResult = mindgage?.result !== null && mindgage?.result !== undefined;
  const resultData = mindgage?.result;
  const isResultArray = Array.isArray(resultData) && resultData.length > 0;

  // Fetch metrics if we have results but no metrics yet
  useEffect(() => {
    if (
      hasResult &&
      isResultArray &&
      !mindgageMetrics?.length &&
      !loadingMetrics
    ) {
      dispatch(fetchMindgageMetrics(currentLang?.value || 'en') as any);
    }
  }, [
    hasResult,
    isResultArray,
    mindgageMetrics,
    loadingMetrics,
    currentLang,
    dispatch
  ]);

  // Process candidate scores and job/profile mindgage metrics
  const candidateData = useMemo(() => {
    if (!isResultArray || !mindgageMetrics?.length) return null;

    // Create a map of metric_id -> score from candidate's mindgage result
    const candidateScoreMap = new Map<number, number>();
    resultData.forEach((item: any) => {
      if (item.metric_id && item.score !== undefined) {
        candidateScoreMap.set(item.metric_id, item.score);
      }
    });

    // Create a map of metric_id -> min_score from job/profile's mindgage_metrics
    // Check both jobItem and profile for mindgage_metrics
    const jobScoreMap = new Map<number, number>();
    const mindgageMetricsSource = jobItem?.mindgage_metrics || profile?.mindgage_metrics;
    if (mindgageMetricsSource && Array.isArray(mindgageMetricsSource)) {
      mindgageMetricsSource.forEach((m: any) => {
        if (m.metric_id && m.min_score !== undefined) {
          jobScoreMap.set(m.metric_id, m.min_score);
        }
      });
    }

    const labels: string[] = [];
    const candidatePoints: number[] = [];
    const companyPoints: (number | null)[] = [];
    const descriptions: any[] = [];

    // If we have job mindgage_metrics, use those to determine which metrics to show
    // Otherwise, show all metrics that the candidate has
    const metricsToShow = jobScoreMap.size > 0
      ? mindgageMetrics.filter((metric: any) => jobScoreMap.has(metric.id))
      : mindgageMetrics;

    metricsToShow.forEach((metric: any) => {
      if (metric.id && metric.name) {
        const candidateScore = candidateScoreMap.get(metric.id);
        if (candidateScore !== undefined) {
          labels.push(metric.name);
          candidatePoints.push(candidateScore);

          // Get job score from profile mindgage_metrics if available
          const jobScore = jobScoreMap.get(metric.id);
          companyPoints.push(jobScore !== undefined ? jobScore : null);

          descriptions.push({
            id: metric.id,
            name: metric.name,
            description: metric.description || ''
          });
        }
      }
    });

    return {
      labels,
      candidatePoints,
      companyPoints,
      descriptions
    };
  }, [resultData, mindgageMetrics, profile, jobItem, isResultArray]);

  // Don't render if no data
  if (!hasResult || !candidateData || candidateData.labels.length === 0) {
    return null;
  }

  const getScoreColor = (score: number) => {
    if (score <= 2) return '#FFE7D9';
    if (score === 3) return '#E9FCD4';
    return '#C8FACD';
  };

  // Prepare data for overlapping chart
  // Show job requirements as main points, candidate scores as comparison
  const chartLabels = candidateData.labels;
  const hasJobRequirements = candidateData.companyPoints.some(
    (p: number | null) => p !== null && p !== undefined
  );
  
  // If we have job requirements, show them as main points and candidate as comparison
  // Otherwise, show candidate scores only
  const chartPoints = hasJobRequirements
    ? candidateData.companyPoints.map((p: number | null) => p || 1)
    : candidateData.candidatePoints;
  
  const comparePoints = hasJobRequirements
    ? candidateData.candidatePoints
    : null;

  // Select icon based on metric name
  const getMetricIcon = (metric: any) => {
    const metricName = metric.name?.toLowerCase() || '';
    if (
      metricName.includes('crunch') ||
      metricName.includes('numeric') ||
      metricName.includes('calculation')
    ) {
      return <CalculateIcon sx={{ fontSize: '30px', color: '#562C82' }} />;
    }
    if (
      metricName.includes('flow') ||
      metricName.includes('rotation') ||
      metricName.includes('spatial')
    ) {
      return <RotateRightIcon sx={{ fontSize: '30px', color: '#562C82' }} />;
    }
    if (
      metricName.includes('seek') ||
      metricName.includes('memory') ||
      metricName.includes('search')
    ) {
      return <SearchIcon sx={{ fontSize: '30px', color: '#562C82' }} />;
    }
    // Default icon for other metrics
    return <PsychologyIcon sx={{ fontSize: '30px', color: '#562C82' }} />;
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        padding: '20px',
        marginTop: '24px'
      }}
    >
      <Grid item xs={12}>
        <Title1
          style={{
            fontWeight: 600,
            fontSize: '20px',
            color: '#131417',
            marginBottom: '20px'
          }}
        >
          {t('Cognitive Ability')}
        </Title1>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card className="open-job-posts-card">
          <CultureProfileCharts
            isMindgage={true}
            title={t('Cognitive Metrics')}
            labels={chartLabels}
            points={chartPoints}
            compareTitle={t('Candidate')}
            comparePoints={comparePoints}
            descriptions={candidateData.descriptions}
            profile={true}
            showTooltips={false}
            minScale={1}
            maxScale={5}
            showCompetencyComparison={
              comparePoints !== null && comparePoints.length > 0
            }
          />
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            padding: '20px'
          }}
        >
          {candidateData.descriptions.map((metric: any, index: number) => {
            const score = candidateData.candidatePoints[index];
            const isOverallScore = metric.name === 'Overall Performance';

            return (
              <Box
                key={metric.id}
                sx={{
                  marginBottom: '24px',
                  paddingBottom: isOverallScore ? '24px' : '0px',
                  borderBottom: isOverallScore ? '1px solid #E0E0E0' : 'none'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px'
                  }}
                >
                  <Box
                    sx={{
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {getMetricIcon(metric)}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#131417'
                    }}
                  >
                    {metric.name}
                  </Typography>
                </Box>

                <Box sx={{ marginBottom: '8px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <Box
                        key={num}
                        sx={{
                          width: '40px',
                          height: '32px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor:
                            num === score
                              ? getScoreColor(score)
                              : 'transparent',
                          border: num === score ? 'none' : '1px solid #E0E0E0',
                          color: num === score ? '#131417' : '#131417',
                          fontWeight: num === score ? 600 : 400,
                          fontSize: '14px'
                        }}
                      >
                        {num}
                      </Box>
                    ))}
                  </Box>
                  <Description2
                    sx={{
                      fontSize: '14px',
                      color: '#666666',
                      lineHeight: '20px'
                    }}
                  >
                    {metric.description}
                  </Description2>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Grid>
    </Grid>
  );
};
